import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import LineChartWidget from './charts/LineChart';
import BarChartWidget from './charts/BarChart';
import PieChartWidget from './charts/PieChart';
import GaugeChartWidget from './charts/GaugeChart';
import TableWidget from './charts/TableWidget';
import HeartbeatWidget from './charts/HeartbeatWidget';
import HeatmapWidget from './charts/HeatmapWidget';
import TimeSeriesTable from './charts/TimeSeriesTable';
import IframeWidget from './charts/IframeWidget';
import FunnelChart from './charts/FunnelChart';
import StatWidget from './charts/StatWidget';
import AreaChartWidget from './charts/AreaChart';
import TopListWidget from './charts/TopListWidget';
import MarkdownWidget from './charts/MarkdownWidget';
import { DashboardVariableBar } from './components/DashboardVariables';
import { withAttribution } from './utils/widgetAttribution';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * Kiosk-mode viewer for {@code /p/:token} URLs. Differs from
 * {@link DashboardViewer}:
 *
 * <ul>
 *   <li>No bearer-token auth — talks to {@code /api/permalinks/public/:token/*}.</li>
 *   <li>No sidebar, no edit, no nav-back; the page is owned by the dashboard.</li>
 *   <li>Auto-refresh defaults to 60 s and never pauses on tab visibility — the
 *       common case is a TV no-one is interacting with.</li>
 *   <li>If the token is expired/revoked, shows a clear "Permalink expired"
 *       state instead of redirecting to login (so the kiosk is honest about
 *       why it stopped working).</li>
 * </ul>
 */
const widgetTypeRenderers = {
  line: LineChartWidget,
  area: AreaChartWidget,
  bar: BarChartWidget,
  pie: PieChartWidget,
  gauge: GaugeChartWidget,
  stat: StatWidget,
  toplist: TopListWidget,
  heatmap: HeatmapWidget,
  heartbeat: HeartbeatWidget,
  table: TableWidget,
  'timeseries-table': TimeSeriesTable,
  funnel: FunnelChart,
  iframe: IframeWidget,
  markdown: MarkdownWidget,
};

const PermalinkViewer = () => {
  const theme = useTheme();
  const { token } = useParams();

  const [dashboard, setDashboard] = useState(null);
  const [permalinkMeta, setPermalinkMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [widgetData, setWidgetData] = useState({});
  const [timeRange, setTimeRange] = useState('1h');
  const [variableValues, setVariableValues] = useState({});

  // Effective time range — locked permalinks override the URL/UI choice.
  const effectiveTimeRange = useMemo(() => {
    if (permalinkMeta?.lockedTimeRange) return permalinkMeta.lockedTimeRange;
    return timeRange;
  }, [permalinkMeta, timeRange]);

  // Variable definitions parsed from dashboard JSON
  const dashboardVariables = useMemo(() => {
    try {
      const arr = JSON.parse(dashboard?.variablesJson || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  }, [dashboard?.variablesJson]);

  // Seed variable values from defaults + locks
  useEffect(() => {
    let lockedVars = {};
    try {
      lockedVars = JSON.parse(permalinkMeta?.lockedVariablesJson || '{}') || {};
    } catch { lockedVars = {}; }
    setVariableValues(prev => {
      const next = { ...prev };
      dashboardVariables.forEach(v => {
        if (lockedVars[v.name] !== undefined) next[v.name] = lockedVars[v.name];
        else if (next[v.name] === undefined) next[v.name] = v.defaultValue ?? '';
      });
      return next;
    });
  }, [dashboardVariables, permalinkMeta]);

  // Fetch dashboard JSON via the public permalink endpoint
  const loadDashboard = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`/api/permalinks/public/${encodeURIComponent(token)}/dashboard`);
      setDashboard(r.data?.dashboard || null);
      setPermalinkMeta(r.data?.permalink || null);
      // If the permalink locked a time range, adopt it as the initial choice
      if (r.data?.permalink?.lockedTimeRange) setTimeRange(r.data.permalink.lockedTimeRange);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error || e?.message || 'Permalink not available';
      setError({ status, msg });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Per-widget fetch via the public SQL endpoint
  const fetchWidgetData = useCallback(async (widget) => {
    const widgetKey = widget.id || widget.tempId;
    const widgetType = widget.type;
    const dataSource = widget.dataSource || (
      widgetType?.startsWith('alert-') ? 'alerts' :
      widgetType?.startsWith('rum-')   ? 'rum'    :
      widgetType === 'iframe'           ? 'custom' :
      widgetType === 'markdown'         ? 'custom' :
      'clickhouse_metrics'
    );

    if (dataSource === 'custom' || widgetType === 'iframe') {
      setWidgetData(p => ({ ...p, [widgetKey]: { type: 'iframe', iframeUrl: widget.iframeUrl } }));
      return;
    }
    if (widgetType === 'markdown') {
      setWidgetData(p => ({ ...p, [widgetKey]: { type: 'markdown' } }));
      return;
    }
    // Alerts + RUM + Inventory kiosk display is intentionally not supported —
    // those backends don't have permalink-aware (unauthenticated) paths yet.
    // Inventory widgets in particular carry a {kind, params} JSON spec, not
    // SQL, so they must never fall through to the public sql-query endpoint.
    if (dataSource === 'alerts' || dataSource === 'rum' || dataSource === 'inventory') {
      const label = dataSource === 'inventory' ? 'Inventory' : 'Alert / RUM';
      setWidgetData(p => ({ ...p, [widgetKey]: { error: `${label} widgets are not yet supported on permalink view` } }));
      return;
    }

    const sqlDataSource = dataSource === 'clickhouse_logs' || dataSource === 'clickhouse_traces'
      ? dataSource : 'clickhouse_metrics';

    try {
      const queries = (widget.queries && widget.queries.length > 0)
        ? widget.queries.filter(q => q.enabled && q.query?.trim())
        : (widget.query ? [{ query: widget.query, alias: widget.name, legendTemplate: '', colorScheme: 'default' }] : []);

      if (queries.length === 0) {
        setWidgetData(p => ({ ...p, [widgetKey]: { type: 'timeseries', data: [] } }));
        return;
      }

      const results = await Promise.all(queries.map(q =>
        // PLAN A2/A3: the widget id for attribution; the server sets the dashboard from the link.
        axios.post(`/api/permalinks/public/${encodeURIComponent(token)}/sql-query`, withAttribution({
          query: q.query,
          timeRange: effectiveTimeRange,
          dataSource: sqlDataSource,
          variables: variableValues,
          chartWidth: window.innerWidth || 1200,
        }, widget, null)).then(r => ({ q, data: r.data }))
      ));

      const merged = { type: 'timeseries', data: [] };
      results.forEach(({ q, data }) => {
        if (data?.type === 'timeseries' && Array.isArray(data.data)) {
          const processed = data.data.map(s => ({
            ...s,
            _queryConfig: { legendTemplate: q.legendTemplate || '', colorScheme: q.colorScheme || 'default', alias: q.alias },
          }));
          merged.data.push(...processed);
        } else if (data?.type === 'scalar') {
          merged.type = 'scalar'; merged.data = data.data; merged.row = data.row; merged.columns = data.columns;
        } else if (data?.type === 'table') {
          merged.type = 'table'; merged.columns = data.columns; merged.rows = data.rows;
        }
      });
      setWidgetData(p => ({ ...p, [widgetKey]: merged }));
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Query failed';
      setWidgetData(p => ({ ...p, [widgetKey]: { error: msg } }));
    }
  }, [token, effectiveTimeRange, variableValues]);

  // Initial fetch + auto-refresh every 60s. Doesn't pause on visibility
  // change — kiosks are typically background-rendered.
  useEffect(() => {
    if (!dashboard?.widgets?.length) return;
    dashboard.widgets.forEach(w => fetchWidgetData(w));
    const t = setInterval(() => {
      dashboard.widgets.forEach(w => fetchWidgetData(w));
    }, 60_000);
    return () => clearInterval(t);
  }, [dashboard, fetchWidgetData]);

  // Re-fetch when variable values or time range change
  useEffect(() => {
    if (dashboard?.widgets?.length) {
      dashboard.widgets.forEach(w => fetchWidgetData(w));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(variableValues), effectiveTimeRange]);

  const layouts = useMemo(() => {
    if (!dashboard?.widgets) return { lg: [] };
    const lg = dashboard.widgets.map(w => ({
      i: String(w.id),
      x: w.gridX || 0, y: w.gridY || 0,
      w: w.gridW || 6, h: w.gridH || 4,
      minW: 2, minH: 2, static: true,
    }));
    return { lg };
  }, [dashboard]);

  if (loading) {
    return (
      <CenteredBox><CircularProgress size={32} /></CenteredBox>
    );
  }
  if (error) {
    return (
      <CenteredBox>
        <Card sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>This permalink is no longer active.</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            It may have expired, been revoked, or never existed. Ask the dashboard owner to issue a new one.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
            HTTP {error.status || '?'} · {error.msg}
          </Typography>
        </Card>
      </CenteredBox>
    );
  }
  if (!dashboard) {
    return <CenteredBox><Typography variant="body2">Dashboard unavailable</Typography></CenteredBox>;
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Minimal header — title + small badge so the kiosk operator knows it's the read-only view */}
      <Box sx={{
        px: 3, py: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {dashboard.name}
          </Typography>
          {dashboard.description && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {dashboard.description}
            </Typography>
          )}
        </Box>
        <Box sx={{
          fontSize: 11, fontWeight: 500, px: 1, py: 0.25, borderRadius: 999,
          bgcolor: 'rgba(9, 105, 218, 0.08)', color: 'primary.main', border: '1px solid',
          borderColor: 'rgba(9, 105, 218, 0.3)',
        }}>
          KIOSK · {permalinkMeta?.label || 'permalink'}
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {effectiveTimeRange}
          {permalinkMeta?.lockedTimeRange && ' (locked)'}
        </Typography>
      </Box>

      <DashboardVariableBar
        variables={dashboardVariables}
        values={variableValues}
        onChange={setVariableValues}
        timeRange={effectiveTimeRange}
      />

      <Box sx={{ flex: 1, p: 1.5, overflow: 'auto' }}>
        {dashboard.widgets.length === 0 ? (
          <CenteredBox><Typography variant="body2">This dashboard has no widgets.</Typography></CenteredBox>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={50}
            isDraggable={false}
            isResizable={false}
            margin={[8, 8]}
            containerPadding={[0, 0]}
          >
            {dashboard.widgets.map(w => {
              const Renderer = widgetTypeRenderers[w.type];
              const data = widgetData[w.id];
              return (
                <Box key={w.id} sx={{
                  bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                  <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{w.name}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0, p: 1, position: 'relative' }}>
                    {data?.error ? (
                      <Alert severity="error" variant="outlined" sx={{ fontSize: 11 }}>{data.error}</Alert>
                    ) : Renderer ? (
                      <Renderer data={data} config={w} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Widget type "{w.type}" not supported in kiosk mode.
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </ResponsiveGridLayout>
        )}
      </Box>
    </Box>
  );
};

const CenteredBox = ({ children }) => (
  <Box sx={{
    width: '100vw', height: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: 'background.default',
  }}>
    <Stack alignItems="center" spacing={1}>{children}</Stack>
  </Box>
);

export default PermalinkViewer;
